"use client";

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, ReactNode } from "react";
import * as React from "react";
import { normalizeHeadingElementTag } from "../ui/lib/textElementTag";
import type { FaqDesignSettings, FaqExpandMode, FaqIconPosition, FaqItem, FaqTriggerIcon, HeadingHtmlTag } from "../ui/types";
import { faqQuestionRowStyles } from "../utils/faqAccordionStyles";
import { buildFaqDesignScopedCss, FAQ_PRESET_STYLE_BUNDLES, faqDesignClassNames, type ResolvedFaqDesign, resolveFaqDesign, resolveFaqDesignPreset } from "./faqDesign";
import { flatElementStylesToReactCss } from "./flatElementStyles";
export type FaqAccordionItem = {
  id?: string;
  question?: string;
  questionHtml?: boolean;
  answer?: string;
  answerHtml?: boolean;
  category?: string;
  iconSvgMarkup?: string;
  itemStyles?: Record<string, unknown>;
  questionStyles?: Record<string, unknown>;
  answerStyles?: Record<string, unknown>;
};
const ALL_CATEGORIES = "__all__";

/** Distinct, order-preserving category labels — derived, never stored alongside. */
function deriveCategories(items: FaqAccordionItem[] | FaqItem[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of items) {
    const label = (it as FaqAccordionItem).category?.trim();
    if (label && !seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}
function matchesQuery(item: FaqAccordionItem, query: string): boolean {
  if (!query) {
    return true;
  }
  const haystack = `${item.question ?? ""} ${item.answer ?? ""}`.toLowerCase();
  return haystack.includes(query);
}
export type FaqAccordionProps = {
  className?: string;
  /** Applied to the currently active chip, on top of `defaultChipStyles`. */
  activeChipStyles?: Record<string, unknown>;
  defaultAnswerStyles?: Record<string, unknown>;
  /** Search/category chip appearance (both chip rows share one style). */
  defaultChipStyles?: Record<string, unknown>;
  defaultItemStyles?: Record<string, unknown>;
  defaultOpenFirst?: boolean;
  defaultQuestionStyles?: Record<string, unknown>;
  design?: FaqDesignSettings | null;
  /** Extra DOM attrs (id, data-pb-node, …) merged onto the root. */
  domProps?: Record<string, unknown>;
  emptyFallback?: ReactNode;
  expandMode?: FaqExpandMode;
  iconPosition?: FaqIconPosition;
  id: string;
  itemGap?: string;
  items: FaqAccordionItem[] | FaqItem[];
  questionTag?: HeadingHtmlTag | string;
  renderAnswer?: (answer: string, item: FaqAccordionItem) => ReactNode;
  renderQuestion?: (question: string, item: FaqAccordionItem) => ReactNode;
  /** Show a search box filtering on question + answer text. */
  searchable?: boolean;
  /** Show filter chips derived from the items' categories. */
  showCategoryFilter?: boolean;
  style?: CSSProperties;
  triggerIcon?: FaqTriggerIcon;
};
function escapePlain(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function FaqTriggerIconView(t0) {
  const $ = _c(2);
  const {
    triggerIcon
  } = t0;
  if (triggerIcon === "none") {
    return null;
  }
  if (triggerIcon === "plus_minus") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span aria-hidden={true} className="pb-faq-icon"><span className="pb-faq-pm"><span className="pb-faq-pm-collapsed">+</span><span className="pb-faq-pm-open">−</span></span></span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden={true} className="pb-faq-icon"><span className="pb-faq-chevron">▸</span></span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Shared FAQ accordion markup for builder canvas and region runtime.
 * Keep both surfaces identical so templates / animation / typography match.
 */
export function FaqAccordion(t0) {
  const $ = _c(92);
  const {
    id,
    items,
    expandMode: t1,
    defaultOpenFirst: t2,
    triggerIcon: t3,
    iconPosition: t4,
    itemGap,
    questionTag,
    defaultItemStyles,
    defaultQuestionStyles,
    defaultAnswerStyles,
    defaultChipStyles,
    activeChipStyles,
    design,
    className,
    style,
    domProps,
    emptyFallback,
    renderAnswer,
    renderQuestion,
    searchable: t5,
    showCategoryFilter: t6
  } = t0;
  const expandMode = t1 === undefined ? "multiple" : t1;
  const defaultOpenFirst = t2 === undefined ? false : t2;
  const triggerIcon = t3 === undefined ? "chevron" : t3;
  const iconPosition = t4 === undefined ? "right" : t4;
  const searchable = t5 === undefined ? false : t5;
  const showCategoryFilter = t6 === undefined ? false : t6;
  const [query, setQuery] = React.useState("");
  const [activeCategory, setActiveCategory] = React.useState(ALL_CATEGORIES);
  let t7;
  if ($[0] !== items || $[1] !== showCategoryFilter) {
    t7 = showCategoryFilter ? deriveCategories(items) : [];
    $[0] = items;
    $[1] = showCategoryFilter;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const categories = t7;
  let t8;
  bb0: {
    if (!(searchable || categories.length > 0)) {
      t8 = items;
      break bb0;
    }
    let t9;
    if ($[3] !== activeCategory || $[4] !== items || $[5] !== query) {
      const needle = query.trim().toLowerCase();
      t9 = (items as FaqAccordionItem[]).filter(it => {
        const inCategory = activeCategory === ALL_CATEGORIES || (it.category?.trim() ?? "") === activeCategory;
        return inCategory && matchesQuery(it, needle);
      });
      $[3] = activeCategory;
      $[4] = items;
      $[5] = query;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    t8 = t9;
  }
  const visibleItems = t8;
  let t9;
  if ($[7] !== defaultChipStyles) {
    t9 = flatElementStylesToReactCss(defaultChipStyles);
    $[7] = defaultChipStyles;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const chipCss = t9;
  let t10;
  if ($[9] !== activeChipStyles) {
    t10 = flatElementStylesToReactCss(activeChipStyles);
    $[9] = activeChipStyles;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== chipCss || $[12] !== t10) {
    t11 = {
      ...chipCss,
      backgroundColor: "var(--tt-color-primary, #0ea5e9)",
      borderColor: "var(--tt-color-primary, #0ea5e9)",
      color: "#fff",
      ...t10
    };
    $[11] = chipCss;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const activeChipCss = t11;
  const presetBundle = FAQ_PRESET_STYLE_BUNDLES[resolveFaqDesignPreset(design)];
  const withPresetFallback = _temp;
  const t12 = defaultItemStyles as Record<string, unknown> | undefined;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[14] !== activeCategory || $[15] !== activeChipCss || $[16] !== categories || $[17] !== chipCss || $[18] !== className || $[19] !== defaultAnswerStyles || $[20] !== defaultOpenFirst || $[21] !== defaultQuestionStyles || $[22] !== design || $[23] !== domProps || $[24] !== emptyFallback || $[25] !== expandMode || $[26] !== iconPosition || $[27] !== id || $[28] !== itemGap || $[29] !== items.length || $[30] !== presetBundle.defaultAnswerStyles || $[31] !== presetBundle.defaultItemStyles || $[32] !== presetBundle.defaultQuestionStyles || $[33] !== query || $[34] !== questionTag || $[35] !== renderAnswer || $[36] !== renderQuestion || $[37] !== searchable || $[38] !== style || $[39] !== t12 || $[40] !== triggerIcon || $[41] !== visibleItems) {
    const defaultItemFlat = withPresetFallback(t12, presetBundle.defaultItemStyles);
    const defaultQuestionFlat = withPresetFallback(defaultQuestionStyles as Record<string, unknown> | undefined, presetBundle.defaultQuestionStyles);
    const defaultAnswerFlat = withPresetFallback(defaultAnswerStyles as Record<string, unknown> | undefined, presetBundle.defaultAnswerStyles);
    const resolved = resolveFaqDesign(design, {
      questionFontSize: typeof defaultQuestionFlat.fontSize === "string" ? defaultQuestionFlat.fontSize : undefined,
      answerFontSize: typeof defaultAnswerFlat.fontSize === "string" ? defaultAnswerFlat.fontSize : undefined
    });
    const scopedCss = buildFaqDesignScopedCss(id, resolved);
    const expandSingle = expandMode === "single";
    const accordionName = expandSingle ? `pb-faq-${id}` : undefined;
    const t23 = questionTag ?? "h3";
    let t24;
    if ($[52] !== t23) {
      t24 = normalizeHeadingElementTag(t23);
      $[52] = t23;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    const qTag = t24;
    const iconLeft = iconPosition === "left";
    let t25;
    if ($[54] !== itemGap) {
      t25 = typeof itemGap === "string" && itemGap.trim() ? itemGap.trim() : undefined;
      $[54] = itemGap;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    const gap = t25;
    let t26;
    if ($[56] !== domProps) {
      t26 = domProps ?? {};
      $[56] = domProps;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    const t27 = t26 as {
      className?: string;
      [key: string]: unknown;
    };
    let domClass;
    let domRest;
    if ($[58] !== t27) {
      ({
        className: domClass,
        ...domRest
      } = t27);
      $[58] = t27;
      $[59] = domClass;
      $[60] = domRest;
    } else {
      domClass = $[59];
      domRest = $[60];
    }
    const rootClass = faqDesignClassNames(id, resolved, [className, domClass].filter(Boolean).join(" ") || undefined);
    const t28 = style?.display ?? "flex";
    const t29 = style?.flexDirection ?? "column";
    const t30 = gap ?? style?.gap;
    let t31;
    if ($[61] !== style || $[62] !== t28 || $[63] !== t29 || $[64] !== t30) {
      t31 = {
        ...style,
        display: t28,
        flexDirection: t29,
        gap: t30
      };
      $[61] = style;
      $[62] = t28;
      $[63] = t29;
      $[64] = t30;
      $[65] = t31;
    } else {
      t31 = $[65];
    }
    const outerStyle = t31;
    let t32;
    if ($[66] !== triggerIcon) {
      t32 = <FaqTriggerIconView triggerIcon={triggerIcon} />;
      $[66] = triggerIcon;
      $[67] = t32;
    } else {
      t32 = $[67];
    }
    const iconBlock = t32;
    t22 = <style dangerouslySetInnerHTML={{
      __html: scopedCss
    }} key={`${id}-pb-faq-scoped-css`} />;
    t13 = domRest;
    t14 = rootClass;
    t15 = id;
    t16 = outerStyle;
    if ($[68] !== query || $[69] !== searchable) {
      t17 = searchable ? <input aria-label="Search questions" className="pb-faq-search" onChange={e => setQuery(e.target.value)} placeholder={"Search questions\u2026"} type="search" value={query} /> : null;
      $[68] = query;
      $[69] = searchable;
      $[70] = t17;
    } else {
      t17 = $[70];
    }
    if ($[71] !== activeCategory || $[72] !== activeChipCss || $[73] !== categories || $[74] !== chipCss) {
      t18 = categories.length > 0 ? <div className="pb-faq-categories" role="tablist">{[ALL_CATEGORIES, ...categories].map(cat => <button aria-selected={activeCategory === cat} className="pb-faq-category" data-active={activeCategory === cat ? "true" : undefined} key={cat} onClick={() => setActiveCategory(cat)} role="tab" style={activeCategory === cat ? activeChipCss : chipCss} type="button">{cat === ALL_CATEGORIES ? "All" : cat}</button>)}</div> : null;
      $[71] = activeCategory;
      $[72] = activeChipCss;
      $[73] = categories;
      $[74] = chipCss;
      $[75] = t18;
    } else {
      t18 = $[75];
    }
    t19 = items.length === 0 ? emptyFallback ?? null : null;
    if ($[76] !== items.length || $[77] !== visibleItems.length) {
      t20 = items.length > 0 && visibleItems.length === 0 ? <p className="pb-faq-no-results">No questions match your search.</p> : null;
      $[76] = items.length;
      $[77] = visibleItems.length;
      $[78] = t20;
    } else {
      t20 = $[78];
    }
    t21 = visibleItems.length === 0 ? null : visibleItems.map((it_0, i) => {
      const itemShell = {
        ...flatElementStylesToReactCss({
          ...defaultItemFlat,
          ...(it_0.itemStyles ?? {})
        } as Record<string, unknown>)
      };
      const {
        summaryRow,
        headingStyle
      } = faqQuestionRowStyles({
        ...defaultQuestionFlat,
        ...(it_0.questionStyles ?? {}),
        ...(resolved.questionFontSize ? {
          fontSize: resolved.questionFontSize
        } : {})
      });
      const answerFlat = flatElementStylesToReactCss({
        ...defaultAnswerFlat,
        ...(it_0.answerStyles ?? {}),
        ...(resolved.answerFontSize ? {
          fontSize: resolved.answerFontSize
        } : {})
      } as Record<string, unknown>);
      const questionText = typeof it_0.question === "string" ? it_0.question : "";
      const answerText = typeof it_0.answer === "string" ? it_0.answer : "";
      const faqItem = it_0 as FaqAccordionItem;
      const renderedQuestion = renderQuestion?.(questionText, faqItem);
      const heading = renderedQuestion ? React.createElement(qTag, {
        className: "pb-faq-q",
        style: headingStyle
      }, renderedQuestion) : faqItem.questionHtml ? React.createElement(qTag, {
        className: "pb-faq-q",
        dangerouslySetInnerHTML: {
          __html: questionText
        },
        style: headingStyle
      }) : React.createElement(qTag, {
        className: "pb-faq-q",
        style: headingStyle
      }, questionText || "Question");
      const itemIconMarkup = faqItem.iconSvgMarkup?.trim();
      const itemIcon = itemIconMarkup ? <span aria-hidden={true} className="pb-faq-item-icon" dangerouslySetInnerHTML={{
        __html: itemIconMarkup
      }} /> : null;
      const summaryChildren = iconLeft ? <>{iconBlock}{itemIcon}{heading}</> : <>{itemIcon}{heading}{iconBlock}</>;
      const renderedAnswer = renderAnswer?.(answerText, faqItem);
      const answerInner = renderedAnswer ? <div className="pb-faq-answer-body" style={answerFlat}>{renderedAnswer}</div> : faqItem.answerHtml ? <div className="pb-faq-answer-body" dangerouslySetInnerHTML={{
        __html: answerText
      }} style={answerFlat} /> : <div className="pb-faq-answer-body" style={answerFlat}>{escapePlain(answerText || "Answer")}</div>;
      const key = typeof it_0.id === "string" && it_0.id ? it_0.id : `${id}-faq-${i}`;
      return <details key={key} name={accordionName} open={defaultOpenFirst && i === 0} style={itemShell}><summary style={summaryRow}>{summaryChildren}</summary><div className="pb-faq-answer">{answerInner}</div></details>;
    });
    $[14] = activeCategory;
    $[15] = activeChipCss;
    $[16] = categories;
    $[17] = chipCss;
    $[18] = className;
    $[19] = defaultAnswerStyles;
    $[20] = defaultOpenFirst;
    $[21] = defaultQuestionStyles;
    $[22] = design;
    $[23] = domProps;
    $[24] = emptyFallback;
    $[25] = expandMode;
    $[26] = iconPosition;
    $[27] = id;
    $[28] = itemGap;
    $[29] = items.length;
    $[30] = presetBundle.defaultAnswerStyles;
    $[31] = presetBundle.defaultItemStyles;
    $[32] = presetBundle.defaultQuestionStyles;
    $[33] = query;
    $[34] = questionTag;
    $[35] = renderAnswer;
    $[36] = renderQuestion;
    $[37] = searchable;
    $[38] = style;
    $[39] = t12;
    $[40] = triggerIcon;
    $[41] = visibleItems;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
    t19 = $[48];
    t20 = $[49];
    t21 = $[50];
    t22 = $[51];
  }
  let t23;
  if ($[79] !== t13 || $[80] !== t14 || $[81] !== t15 || $[82] !== t16 || $[83] !== t17 || $[84] !== t18 || $[85] !== t19 || $[86] !== t20 || $[87] !== t21) {
    t23 = <div {...t13} className={t14} data-pb-node={t15} style={t16}>{t17}{t18}{t19}{t20}{t21}</div>;
    $[79] = t13;
    $[80] = t14;
    $[81] = t15;
    $[82] = t16;
    $[83] = t17;
    $[84] = t18;
    $[85] = t19;
    $[86] = t20;
    $[87] = t21;
    $[88] = t23;
  } else {
    t23 = $[88];
  }
  let t24;
  if ($[89] !== t22 || $[90] !== t23) {
    t24 = <>{t22}{t23}</>;
    $[89] = t22;
    $[90] = t23;
    $[91] = t24;
  } else {
    t24 = $[91];
  }
  return t24;
}
function _temp(own, fromPreset) {
  return {
    ...(fromPreset as Record<string, unknown>),
    ...own
  };
}